/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import type { LatLngTuple } from "leaflet";
import * as T from "@goodgym/graphql/types";

export type TaskRequestMapProps = {
  taskRequest: T.TaskRequestFragment | T.CoachReferralFragment;
  height?: number | string;
  minHeight?: number | string;
  style?: React.CSSProperties;
  className?: string;
  fitCircle?: boolean;
  padding?: number;
  radius?: number;
  promo?: boolean;
};

const TaskRequestMap: React.FC<TaskRequestMapProps> = ({
  taskRequest,
  height,
  promo,
  minHeight,
  style,
  className,
  fitCircle = true,
  padding = 24,
  radius = 200,
}) => {
  const [MapComponents, setMapComponents] = useState<any>(null);
  const [map, setMap] = useState<L.Map | null>(null);
  const [containerReady, setContainerReady] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    let observer: ResizeObserver;

    const checkAndObserve = () => {
      const el = containerRef.current;
      if (!el) {
        requestAnimationFrame(checkAndObserve);
        return;
      }

      observer = new ResizeObserver(([entry]) => {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) setContainerReady(true);
      });

      observer.observe(el);
    };

    requestAnimationFrame(checkAndObserve);
    return () => observer?.disconnect();
  }, []);

  useEffect(() => {
    import("react-leaflet").then((mod) => {
      setMapComponents({
        MapContainer: mod.MapContainer,
        TileLayer: mod.TileLayer,
        Circle: mod.Circle,
      });
    });
  }, []);

  useEffect(() => {
    // @ts-ignore
    import("leaflet/dist/leaflet.css");
  }, []);

  useEffect(() => {
    if (!map || !containerReady || typeof window === "undefined") return;
    if (
      typeof taskRequest.lat !== "number" ||
      typeof taskRequest.lng !== "number"
    )
      return;
    if (!fitCircle) return;
  }, [
    map,
    containerReady,
    taskRequest.lat,
    taskRequest.lng,
    fitCircle,
    padding,
    radius,
    height,
    minHeight,
  ]);

  if (
    typeof taskRequest.lat !== "number" ||
    typeof taskRequest.lng !== "number" ||
    typeof window === "undefined" ||
    !MapComponents
  ) {
    return null;
  }

  const { MapContainer, TileLayer, Circle } = MapComponents;
  const center: LatLngTuple = [taskRequest.lat, taskRequest.lng];

  const containerMinHeight = minHeight ?? 300;
  const containerHeight = height ?? 350;

  return (
    <div
      ref={(el) => (containerRef.current = el)}
      className={className}
      style={{
        width: "100%",
        minHeight:
          typeof containerMinHeight === "number"
            ? `${containerMinHeight}px`
            : containerMinHeight,
        height:
          typeof containerHeight === "number"
            ? `${containerHeight}px`
            : containerHeight,
        overflow: "hidden",
        ...style,
      }}
    >
      {containerReady && (
        <MapContainer
          center={center}
          zoom={promo ? 14.2 : 15}
          scrollWheelZoom={false}
          whenCreated={setMap}
          style={{ height: "100%", width: "100%", zIndex: 1 }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Circle
            center={center}
            radius={radius}
            pathOptions={{
              color: "#E11018",
              fillColor: "#E11018",
              fillOpacity: 0.1,
              opacity: 0.5,
            }}
          />
        </MapContainer>
      )}
    </div>
  );
};

export default TaskRequestMap;
